import { CheckOutlined, CloseOutlined, FrownFilled, SmileOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { AxiosError } from 'axios';
import SymptomTag from 'components/SymptomTag';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader } from 'components/ui/empty';
import { Spinner } from 'components/ui/spinner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';

export type PatientProfileModalProps = {
  patientId: string;
  patientName: string;
  open?: boolean;
  onClose?: () => void;
};

const PatientProfileModal: React.FC<PatientProfileModalProps> = ({
  patientId,
  patientName,
  open = false,
  onClose = undefined,
}) => {
  // Hooks
  const { t } = useTranslation();

  // State
  const [isModalOpen, setIsModalOpen] = useState(open);

  const { data: survey, loading: loadingOnboardingSurvey } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurvey(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the onboarding survey
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const { data: surveyV2, loading: loadingOnboardingSurveyV2 } = useRequest(
    () => ApiSymptomTrackingManager.getOnboardingSurveyV2(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the onboarding survey v2
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const { data: file, loading: loadingPatientFile } = useRequest(
    () => ApiSymptomTrackingManager.getPatientFile(patientId),
    {
      onError: error => {
        // ignore 404 errors as not all users have filled out the patient file
        if (!(error instanceof AxiosError && error.response?.status === 404)) {
          console.error(error);
        }
      },
    },
  );

  const isLoading = loadingOnboardingSurvey || loadingOnboardingSurveyV2 || loadingPatientFile;

  const handleOk = () => {
    setIsModalOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const noAnswer = (text: string) => <p className="italic">{text}</p>;
  const extraField = (text: string) => <p className="rounded-md bg-background p-2 italic">{text}</p>;
  const sectionTitle = (text: string) => <h4 className="mt-6 mb-1 text-subtle-foreground">{text}</h4>;

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleOk();
      }}
    >
      <DialogContent showCloseButton={false} aria-describedby={undefined}>
        <div className="-mt-4 flex h-full flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-6" />
            </div>
          ) : (
            <>
              {!survey && !surveyV2 && !file && (
                <Empty>
                  <EmptyHeader>
                    <EmptyDescription>{t('patientProfile.noData')}</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              {(survey || surveyV2) && file && (
                <div className="mx-auto my-2.5">
                  <DialogTitle asChild>
                    <h2>{patientName}</h2>
                  </DialogTitle>
                  <p className="mt-0 text-subtle-foreground">
                    {t('patientProfile.bornIn', {
                      year: file.birth_year,
                      age: new Date().getFullYear() - file.birth_year,
                    })}
                  </p>

                  {/* Display new questionnaire if available (prioritize V2) */}
                  {surveyV2 ? (
                    <>
                      {sectionTitle(t('patientProfile.pathologies'))}
                      <div className="flex flex-wrap gap-2">
                        {surveyV2.pathologies.map(pathology => (
                          <Badge key={pathology} variant="secondary">
                            {t(`onboarding.questionnaire.pathologies.options.${pathology}`)}
                          </Badge>
                        ))}
                      </div>
                      {surveyV2.extra_pathologies && extraField(surveyV2.extra_pathologies)}
                      {surveyV2.pathologies.length === 0 &&
                        !surveyV2.extra_pathologies &&
                        noAnswer(t('patientProfile.noPathologies'))}

                      {sectionTitle(t('patientProfile.chronicSymptoms'))}
                      {surveyV2.chronic_symptom_duration
                        ? t(`onboarding.questionnaire.chronicSymptoms.options.${surveyV2.chronic_symptom_duration}`)
                        : noAnswer(t('patientProfile.noAnswer'))}

                      {surveyV2.chronic_symptom_duration && surveyV2.chronic_symptom_duration !== 'NO' && (
                        <>
                          {sectionTitle(t('patientProfile.symptomImpact'))}
                          {surveyV2.symptom_impact
                            ? t(`onboarding.questionnaire.chronicSymptoms.impact.options.${surveyV2.symptom_impact}`)
                            : noAnswer(t('patientProfile.noAnswer'))}

                          {sectionTitle(t('patientProfile.consultationCount'))}
                          {surveyV2.consultation_count
                            ? t(
                                `onboarding.questionnaire.chronicSymptoms.consultation.options.${surveyV2.consultation_count}`,
                              )
                            : noAnswer(t('patientProfile.noAnswer'))}
                        </>
                      )}
                    </>
                  ) : (
                    /* Display old questionnaire if V2 is not available */
                    survey && (
                      <>
                        {sectionTitle(t('patientProfile.feels_pain_or_discomfort'))}
                        {survey.feels_pain_or_discomfort ? (
                          <Badge variant="destructive">
                            <FrownFilled />
                            {t('patientProfile.yes')}
                          </Badge>
                        ) : (
                          <Badge variant="success">
                            <SmileOutlined />
                            {t('patientProfile.no')}
                          </Badge>
                        )}

                        {sectionTitle(t('patientProfile.symptoms'))}
                        {survey.symptoms.map(symptom => (
                          <SymptomTag key={symptom} symptom={symptom} />
                        ))}
                        {survey.extra_symptoms && extraField(survey.extra_symptoms)}
                        {survey.symptoms.length === 0 &&
                          !survey.extra_symptoms &&
                          noAnswer(t('patientProfile.noSymptoms'))}

                        {sectionTitle(t('patientProfile.pathologies'))}
                        <div className="flex flex-wrap gap-2">
                          {survey.pathologies.map(pathology => (
                            <Badge key={pathology} variant="secondary">
                              {t(`onboarding.questionnaire.pathologies.options.${pathology}`)}
                            </Badge>
                          ))}
                        </div>
                        {survey.extra_pathologies && extraField(survey.extra_pathologies)}
                        {survey.pathologies.length === 0 &&
                          !survey.extra_pathologies &&
                          noAnswer(t('patientProfile.noPathologies'))}

                        {sectionTitle(t('patientProfile.expectations'))}
                        <div className="flex flex-wrap gap-2">
                          {survey.expectations.map(expectation => (
                            <Badge key={expectation} variant="secondary">
                              {t(`onboarding.questionnaire.expectations.options.${expectation}`)}
                            </Badge>
                          ))}
                        </div>
                        {survey.extra_expectations && extraField(survey.extra_expectations)}
                        {survey.expectations.length === 0 &&
                          !survey.extra_expectations &&
                          noAnswer(t('patientProfile.noExpectations'))}

                        {sectionTitle(t('patientProfile.questions'))}
                        <div className="flex flex-wrap gap-2">
                          {survey.questions.map(question => (
                            <Badge key={question} variant="secondary">
                              {t(`onboarding.questionnaire.questions.options.${question}`)}
                            </Badge>
                          ))}
                        </div>
                        {survey.extra_questions && extraField(survey.extra_questions)}
                        {survey.questions.length === 0 &&
                          !survey.extra_questions &&
                          noAnswer(t('patientProfile.noQuestions'))}
                      </>
                    )
                  )}

                  {sectionTitle(t('patientProfile.file'))}
                  <p>
                    {file.takes_contraception ? (
                      <>
                        <CheckOutlined className="mr-2 text-chart-severity-very-good" />
                        {t('patientProfile.takesContraception')}
                      </>
                    ) : (
                      <>
                        <CloseOutlined className="mr-2 text-chart-severity-very-bad" />
                        {t('patientProfile.takesNoContraception')}
                      </>
                    )}
                  </p>
                  <p>
                    {file.has_children ? (
                      <>
                        <CheckOutlined className="mr-2 text-chart-severity-very-good" />
                        {t('patientProfile.hasChildren')}
                      </>
                    ) : (
                      <>
                        <CloseOutlined className="mr-2 text-chart-severity-very-bad" />
                        {t('patientProfile.hasNoChildren')}
                      </>
                    )}
                  </p>
                  <p>
                    {file.is_pregnant ? (
                      <>
                        <CheckOutlined className="mr-2 text-chart-severity-very-good" />
                        {t('patientProfile.isPregnant')}
                      </>
                    ) : (
                      <>
                        <CloseOutlined className="mr-2 text-chart-severity-very-bad" />
                        {t('patientProfile.isNotPregnant')}
                      </>
                    )}
                  </p>
                  <p>
                    {file.is_trying_to_conceive ? (
                      <>
                        <CheckOutlined className="mr-2 text-chart-severity-very-good" />
                        {t('patientProfile.isTryingToConceive')}
                      </>
                    ) : (
                      <>
                        <CloseOutlined className="mr-2 text-chart-severity-very-bad" />
                        {t('patientProfile.isNotTryingToConceive')}
                      </>
                    )}
                  </p>
                  <p>
                    {file.is_taking_treatment ? (
                      <>
                        <CheckOutlined className="mr-2 text-chart-severity-very-good" />
                        {t('patientProfile.isTakingTreatment')}
                      </>
                    ) : (
                      <>
                        <CloseOutlined className="mr-2 text-chart-severity-very-bad" />
                        {t('patientProfile.isNotTakingTreatment')}
                      </>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleOk}>{t('global.buttons.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export default PatientProfileModal;
